import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useStore, newControllerId, type View } from '../state/store'
import { makeController } from './SidePanel'
import { Inspector } from './Inspector'
import { IconFit, IconRuler } from './icons'
import type { Controller, PathDef, Vec2 } from '../core/types'
import {
  controllerSpine,
  offsetSpine,
  propagateSync,
  sampleCatmullRom,
  spineDistSide,
} from '../core/curves'
import { boundaryBBox, sanitizeBoundaryNodes } from '../core/boundary'
import { snapWorldPoint, type SnapGuide } from './snap'

// Which part of a controller a pointer interaction grabbed. 'a'/'b' are the
// line endpoints, {node} a curve/polyline node, 'both' the whole controller,
// 'radius' the influence ring edge.
type Part = 'a' | 'b' | 'both' | 'radius' | { node: number }

// Width of the edge rulers in screen pixels.
const RULER = 22

type Drag =
  | { kind: 'pan'; startX: number; startY: number; view: View }
  | { kind: 'ruler' }
  | { kind: 'guide'; id: string; axis: 'x' | 'y' }
  | {
      kind: 'controller'
      id: string
      part: Exclude<Part, 'radius'>
      startWorld: Vec2
      origA: Vec2
      origB: Vec2
      origNodes: Vec2[]
    }
  | { kind: 'radius'; id: string }
  | { kind: 'path'; id: string; part: 'both' | { node: number }; startWorld: Vec2; origNodes: Vec2[] }
  | { kind: 'boundaryNode'; index: number; startWorld: Vec2; orig: Vec2 }
  | null

interface Menu {
  x: number
  y: number
  id: string
  target: 'controller' | 'path'
  kind: Controller['kind'] | PathDef['kind']
}

// Resolve a design token at draw time so the preview follows the theme.
function makeTokens() {
  const css = getComputedStyle(document.documentElement)
  return (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback
}

const NODE_MERGE_MM = 0.5

export function CanvasView() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const doc = useStore((s) => s.doc)
  const result = useStore((s) => s.result)
  const activeChannel = useStore((s) => s.activeChannel)
  const selectedController = useStore((s) => s.selectedController)
  const setSelectedController = useStore((s) => s.setSelectedController)
  const pendingAdd = useStore((s) => s.pendingAdd)
  const setPendingAdd = useStore((s) => s.setPendingAdd)
  const commit = useStore((s) => s.commit)
  const live = useStore((s) => s.live)
  const beginGesture = useStore((s) => s.beginGesture)
  const endGesture = useStore((s) => s.endGesture)
  const view = useStore((s) => s.view)
  const setView = useStore((s) => s.setView)
  const fitTick = useStore((s) => s.fitTick)
  const theme = useStore((s) => s.theme)

  const [hover, setHover] = useState<Vec2 | null>(null)
  const [menu, setMenu] = useState<Menu | null>(null)
  const [rulerOn, setRulerOn] = useState(false)
  const [ruler, setRuler] = useState<{ a: Vec2; b: Vec2 } | null>(null)
  const snap = useStore((s) => s.snap)
  const [guides, setGuides] = useState<SnapGuide[]>([])
  const [cursorPx, setCursorPx] = useState<{ x: number; y: number } | null>(null)
  const [guideLabel, setGuideLabel] = useState<{ axis: 'x' | 'y'; value: number; sx: number; sy: number } | null>(null)

  // The panel path is rebuilt only when the generation result changes, not on
  // every redraw (cursor tracking redraws each frame; at tens of thousands of
  // holes the Path2D rebuild would dominate).
  const panelPaths = useMemo(() => {
    if (!result) return null
    const path = new Path2D()
    addRing(path, result.boundary)
    for (const hole of result.holes) {
      if (hole.circle) {
        path.moveTo(hole.circle.cx + hole.circle.r, hole.circle.cy)
        path.arc(hole.circle.cx, hole.circle.cy, hole.circle.r, 0, Math.PI * 2)
      } else {
        for (const ring of hole.poly) addRing(path, ring)
      }
    }
    const outline = new Path2D()
    addRing(outline, result.boundary)
    return { path, outline }
  }, [result])

  // Snap a world point when snapping is on; live drags surface guide lines.
  const applySnap = useCallback(
    (w: Vec2, showGuides: boolean): Vec2 => {
      if (!snap || !view) return w
      const res = snapWorldPoint(doc, w, 8 / view.scale)
      if (showGuides) setGuides(res.guides)
      return { x: res.x, y: res.y }
    },
    [snap, view, doc],
  )
  const dragRef = useRef<Drag>(null)
  const spaceRef = useRef(false)

  // Background image element, decoded off the document's data URI.
  const bgRef = useRef<{ uri: string; img: HTMLImageElement } | null>(null)
  const [bgTick, setBgTick] = useState(0)
  const bgUri = doc.background?.dataUri
  useEffect(() => {
    if (!bgUri) {
      bgRef.current = null
      return
    }
    if (bgRef.current?.uri === bgUri) return
    const img = new Image()
    img.onload = () => {
      bgRef.current = { uri: bgUri, img }
      setBgTick((t) => t + 1)
    }
    img.src = bgUri
  }, [bgUri])

  const fit = useCallback(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const rect = wrap.getBoundingClientRect()
    const margin = 40
    const scale = Math.min(
      (rect.width - RULER - margin * 2) / doc.canvas.width,
      (rect.height - RULER - margin * 2) / doc.canvas.height,
    )
    setView({
      scale,
      tx: RULER + (rect.width - RULER - doc.canvas.width * scale) / 2,
      ty: RULER + (rect.height - RULER - doc.canvas.height * scale) / 2,
    })
  }, [doc.canvas.width, doc.canvas.height, setView])

  // Fit on mount, when the mm canvas size changes, and on request (palette, F key).
  useEffect(() => fit(), [fit, fitTick])

  // Track the wrapper size for the backing store resolution.
  const [wrapSize, setWrapSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const obs = new ResizeObserver(() => {
      const r = wrap.getBoundingClientRect()
      setWrapSize({ w: r.width, h: r.height })
    })
    obs.observe(wrap)
    return () => obs.disconnect()
  }, [])

  // The context menu closes on Escape or any press outside it. The capture
  // phase lets Escape dismiss only the menu, before the app-level handler
  // would also clear the selection (progressive dismissal).
  useEffect(() => {
    if (!menu) return
    const onDown = (e: PointerEvent) => {
      if (!(e.target as HTMLElement | null)?.closest?.('.context-menu')) setMenu(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setMenu(null)
      }
    }
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [menu])

  // Space key toggles pan mode.
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement)) {
        spaceRef.current = true
        e.preventDefault()
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceRef.current = false
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [])

  const toWorld = useCallback(
    (sx: number, sy: number): Vec2 => {
      if (!view) return { x: 0, y: 0 }
      return { x: (sx - view.tx) / view.scale, y: (sy - view.ty) / view.scale }
    },
    [view],
  )

  // Cursor-centered zoom; registered manually so preventDefault works.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const v = useStore.getState().view
      if (!v) return
      const rect = canvas.getBoundingClientRect()
      const sx = e.clientX - rect.left
      const sy = e.clientY - rect.top
      const factor = Math.exp(-e.deltaY * 0.0015)
      const scale = Math.min(200, Math.max(0.05, v.scale * factor))
      const wx = (sx - v.tx) / v.scale
      const wy = (sy - v.ty) / v.scale
      setView({ scale, tx: sx - wx * scale, ty: sy - wy * scale })
    }
    canvas.addEventListener('wheel', onWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', onWheel)
  }, [setView])

  const controllers = doc.channels[activeChannel].controllers
  const pathMode = doc.layout.mode === 'path'
  const selectedPath = useStore((s) => s.selectedPath)
  const setSelectedPath = useStore((s) => s.setSelectedPath)

  const pathSpine = useCallback(
    (p: PathDef): Vec2[] => (p.kind === 'curve' ? sampleCatmullRom(p.nodes) : p.nodes),
    [],
  )

  // Screen-space distance helper.
  const toScreen = useCallback(
    (p: Vec2) => ({
      x: p.x * (view?.scale ?? 1) + (view?.tx ?? 0),
      y: p.y * (view?.scale ?? 1) + (view?.ty ?? 0),
    }),
    [view],
  )

  const hitController = useCallback(
    (sx: number, sy: number): { c: Controller; part: Part } | null => {
      if (!view) return null
      const hitR = 10
      const w = toWorld(sx, sy)
      // Handles first, topmost controller wins.
      for (let i = controllers.length - 1; i >= 0; i--) {
        const c = controllers[i]
        if (c.kind === 'curve' || c.kind === 'polyline') {
          for (let n = c.nodes.length - 1; n >= 0; n--) {
            const s = toScreen(c.nodes[n])
            if (Math.hypot(sx - s.x, sy - s.y) < hitR) return { c, part: { node: n } }
          }
        } else {
          const sa = toScreen(c.a)
          if (Math.hypot(sx - sa.x, sy - sa.y) < hitR) return { c, part: 'a' }
          if (c.kind === 'line') {
            const sb = toScreen(c.b)
            if (Math.hypot(sx - sb.x, sy - sb.y) < hitR) return { c, part: 'b' }
          }
        }
      }
      // Influence ring and spine are drawn (and grabbable) only on the
      // selected controller; unselected ones show just their handle dots.
      const sel = controllers.find((c) => c.id === selectedController)
      if (sel && sel.enabled) {
        const { d } = spineDistSide(controllerSpine(sel), w.x, w.y, sel.infinite && sel.kind !== 'point')
        if (Math.abs(d - sel.radius) * view.scale < hitR * 0.7) return { c: sel, part: 'radius' }
        if (sel.kind !== 'point') {
          const { d: ds } = spineDistSide(controllerSpine(sel), w.x, w.y, false)
          if (ds * view.scale < hitR * 0.7) return { c: sel, part: 'both' }
        }
      }
      return null
    },
    [view, controllers, selectedController, toWorld, toScreen],
  )

  const hitPath = useCallback(
    (sx: number, sy: number): { p: PathDef; part: 'both' | { node: number } } | null => {
      if (!view || !pathMode) return null
      const hitR = 10
      const w = toWorld(sx, sy)
      const paths = doc.layout.paths
      for (let i = paths.length - 1; i >= 0; i--) {
        const p = paths[i]
        for (let n = p.nodes.length - 1; n >= 0; n--) {
          const s = toScreen(p.nodes[n])
          if (Math.hypot(sx - s.x, sy - s.y) < hitR) return { p, part: { node: n } }
        }
      }
      for (let i = paths.length - 1; i >= 0; i--) {
        const p = paths[i]
        const { d } = spineDistSide(pathSpine(p), w.x, w.y, false)
        if (d * view.scale < hitR * 0.7) return { p, part: 'both' }
      }
      return null
    },
    [view, pathMode, doc.layout.paths, toWorld, toScreen, pathSpine],
  )

  const boundaryEditable = doc.boundary.kind === 'polygon' && doc.boundary.nodes.length >= 3

  const hitBoundaryNode = useCallback(
    (sx: number, sy: number): number | null => {
      if (!view || !boundaryEditable) return null
      const hitR = 10
      for (let i = doc.boundary.nodes.length - 1; i >= 0; i--) {
        const s = toScreen(doc.boundary.nodes[i])
        if (Math.hypot(sx - s.x, sy - s.y) < hitR) return i
      }
      return null
    },
    [view, boundaryEditable, doc.boundary.nodes, toScreen],
  )

  const finishPending = useCallback(() => {
    const pending = useStore.getState().pendingAdd
    if (!pending || (pending.kind !== 'curve' && pending.kind !== 'polyline')) return
    const nodes = [...pending.nodes]
    // A finishing double-click appends coincident nodes; merge them.
    while (
      nodes.length >= 2 &&
      Math.hypot(
        nodes[nodes.length - 1].x - nodes[nodes.length - 2].x,
        nodes[nodes.length - 1].y - nodes[nodes.length - 2].y,
      ) < NODE_MERGE_MM
    ) {
      nodes.pop()
    }
    if (pending.target === 'boundary') {
      const clean = nodes.length >= 3 ? sanitizeBoundaryNodes(nodes) : null
      if (clean) {
        commit('Draw boundary', (d) => {
          d.boundary.kind = 'polygon'
          d.boundary.nodes = clean
        })
      } else {
        useStore.getState().pushToast('A boundary needs at least three corners enclosing an area', 'warn')
      }
      setPendingAdd(null)
      return
    }
    if (nodes.length >= 2) {
      if (pending.target === 'path') {
        const kind = pending.kind as PathDef['kind']
        commit(`Add ${kind} path`, (d) => {
          d.layout.paths.push({ id: 'p' + newControllerId().slice(1), kind, nodes })
        })
      } else {
        const base = doc.channels[pending.channel].base
        const created = makeController(
          pending.kind,
          pending.channel,
          nodes[0],
          nodes[nodes.length - 1],
          base,
          nodes,
        )
        commit(`Add ${pending.kind} controller`, (d) => {
          d.channels[pending.channel].controllers.push(created)
        })
        setSelectedController(created.id)
      }
    }
    setPendingAdd(null)
  }, [doc.channels, commit, setPendingAdd, setSelectedController])

  const onPointerDown = (e: React.PointerEvent) => {
    const canvas = canvasRef.current
    if (!canvas || !view) return
    canvas.setPointerCapture(e.pointerId)
    canvas.focus()
    setMenu(null)
    const rect = canvas.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top

    if (e.button === 1 || spaceRef.current) {
      dragRef.current = { kind: 'pan', startX: sx, startY: sy, view }
      return
    }
    if (e.button !== 0) return

    // Pull a measurement guide out of a ruler: the left ruler yields a
    // vertical guide, the top ruler a horizontal one.
    if (sx < RULER || sy < RULER) {
      if (sx < RULER && sy < RULER) return // corner square
      const axis: 'x' | 'y' = sx < RULER ? 'x' : 'y'
      const id = 'g' + newControllerId().slice(1)
      const w = toWorld(sx, sy)
      beginGesture('Add guide')
      live((d) => {
        d.guides.push({ id, axis, value: axis === 'x' ? w.x : w.y })
      })
      dragRef.current = { kind: 'guide', id, axis }
      return
    }

    if (rulerOn) {
      const w = applySnap(toWorld(sx, sy), false)
      setRuler({ a: w, b: w })
      dragRef.current = { kind: 'ruler' }
      return
    }

    if (pendingAdd) {
      const w = applySnap(toWorld(sx, sy), false)
      if (pendingAdd.kind === 'point') {
        const base = doc.channels[pendingAdd.channel].base
        const created = makeController('point', pendingAdd.channel, w, w, base)
        commit('Add point controller', (d) => {
          d.channels[pendingAdd.channel].controllers.push(created)
        })
        setSelectedController(created.id) // new controllers open for editing
        setPendingAdd(null)
      } else if (pendingAdd.kind === 'line') {
        if (!pendingAdd.firstPoint) {
          setPendingAdd({ ...pendingAdd, firstPoint: w })
        } else {
          const base = doc.channels[pendingAdd.channel].base
          const first = pendingAdd.firstPoint
          const created = makeController('line', pendingAdd.channel, first, w, base)
          commit('Add line controller', (d) => {
            d.channels[pendingAdd.channel].controllers.push(created)
          })
          setSelectedController(created.id)
          setPendingAdd(null)
        }
      } else {
        // Curve/polyline: accumulate nodes; double-click or Enter finishes.
        setPendingAdd({ ...pendingAdd, nodes: [...pendingAdd.nodes, w] })
      }
      return
    }

    if (pathMode) {
      const ph = hitPath(sx, sy)
      if (ph) {
        setSelectedPath(ph.p.id)
        setSelectedController(null)
        beginGesture('Move path')
        dragRef.current = {
          kind: 'path',
          id: ph.p.id,
          part: ph.part,
          startWorld: toWorld(sx, sy),
          origNodes: ph.p.nodes.map((n) => ({ ...n })),
        }
        return
      }
    }

    const bIdx = hitBoundaryNode(sx, sy)
    if (bIdx !== null) {
      beginGesture('Move boundary corner')
      dragRef.current = {
        kind: 'boundaryNode',
        index: bIdx,
        startWorld: toWorld(sx, sy),
        orig: { ...doc.boundary.nodes[bIdx] },
      }
      return
    }

    const hit = hitController(sx, sy)
    if (hit) {
      setSelectedController(hit.c.id)
      if (hit.part === 'radius') {
        beginGesture('Resize influence')
        dragRef.current = { kind: 'radius', id: hit.c.id }
      } else {
        beginGesture('Move controller')
        dragRef.current = {
          kind: 'controller',
          id: hit.c.id,
          part: hit.part,
          startWorld: toWorld(sx, sy),
          origA: { ...hit.c.a },
          origB: { ...hit.c.b },
          origNodes: hit.c.nodes.map((p) => ({ ...p })),
        }
      }
      return
    }
    // Grab an existing guide line.
    for (const g of doc.guides) {
      const s = g.value * view.scale + (g.axis === 'x' ? view.tx : view.ty)
      if (Math.abs(s - (g.axis === 'x' ? sx : sy)) < 5) {
        beginGesture('Move guide')
        dragRef.current = { kind: 'guide', id: g.id, axis: g.axis }
        setSelectedController(null)
        return
      }
    }

    // Empty click: start panning as a convenience.
    dragRef.current = { kind: 'pan', startX: sx, startY: sy, view }
    setSelectedController(null)
    setSelectedPath(null)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    setCursorPx({ x: sx, y: sy })
    if (pendingAdd) {
      // Snapped hover keeps the placement preview honest about where the
      // click will land, guides included.
      setHover(applySnap(toWorld(sx, sy), true))
    } else if (hover) {
      setHover(null)
      setGuides([])
    }

    const drag = dragRef.current
    if (!drag) return
    if (drag.kind === 'guide') {
      const w = toWorld(sx, sy)
      const raw = drag.axis === 'x' ? w.x : w.y
      let val = Math.round(raw * 10) / 10
      if (snap && view) {
        const res = snapWorldPoint(doc, w, 8 / view.scale, drag.id)
        val = drag.axis === 'x' ? res.x : res.y
      }
      live((d) => {
        const g = d.guides.find((x) => x.id === drag.id)
        if (g) g.value = val
      })
      setGuideLabel({ axis: drag.axis, value: val, sx, sy })
      return
    }
    if (drag.kind === 'pan') {
      setView({
        scale: drag.view.scale,
        tx: drag.view.tx + (sx - drag.startX),
        ty: drag.view.ty + (sy - drag.startY),
      })
      return
    }
    const w = toWorld(sx, sy)
    if (drag.kind === 'ruler') {
      const b = applySnap(w, false)
      setRuler((r) => (r ? { a: r.a, b } : null))
      return
    }
    if (drag.kind === 'boundaryNode') {
      const target = applySnap(
        { x: drag.orig.x + (w.x - drag.startWorld.x), y: drag.orig.y + (w.y - drag.startWorld.y) },
        true,
      )
      live((d) => {
        const node = d.boundary.nodes[drag.index]
        if (node) d.boundary.nodes[drag.index] = target
      })
      return
    }
    if (drag.kind === 'path') {
      const dx = w.x - drag.startWorld.x
      const dy = w.y - drag.startWorld.y
      if (drag.part === 'both') {
        const ref = drag.origNodes[0]
        const snapped = applySnap({ x: ref.x + dx, y: ref.y + dy }, true)
        const ddx = snapped.x - ref.x
        const ddy = snapped.y - ref.y
        live((d) => {
          const p = d.layout.paths.find((x) => x.id === drag.id)
          if (p) p.nodes = drag.origNodes.map((n) => ({ x: n.x + ddx, y: n.y + ddy }))
        })
      } else {
        const orig = drag.origNodes[drag.part.node]
        if (!orig) return
        const target = applySnap({ x: orig.x + dx, y: orig.y + dy }, true)
        const idx = drag.part.node
        live((d) => {
          const p = d.layout.paths.find((x) => x.id === drag.id)
          if (p) p.nodes[idx] = target
        })
      }
      return
    }
    if (drag.kind === 'radius') {
      live((d) => {
        const c = d.channels[activeChannel].controllers.find((x) => x.id === drag.id)
        if (!c) return
        const { d: dist } = spineDistSide(controllerSpine(c), w.x, w.y, c.infinite && c.kind !== 'point')
        // With snapping on, influence radii land on whole millimetres.
        const rounded = snap ? Math.round(dist) : Math.round(dist * 10) / 10
        c.radius = Math.max(1, Math.min(500, rounded))
        propagateSync(d, c.id)
      })
      return
    }
    const dx = w.x - drag.startWorld.x
    const dy = w.y - drag.startWorld.y
    const part = drag.part
    // Snap the handle being dragged (or the anchor for whole-controller
    // moves), then derive the applied delta from the snapped position.
    let snapped: Vec2 | null = null
    if (part === 'a' || part === 'both') {
      snapped = applySnap({ x: drag.origA.x + dx, y: drag.origA.y + dy }, true)
    } else if (part === 'b') {
      snapped = applySnap({ x: drag.origB.x + dx, y: drag.origB.y + dy }, true)
    } else if (typeof part === 'object') {
      const orig = drag.origNodes[part.node]
      if (orig) snapped = applySnap({ x: orig.x + dx, y: orig.y + dy }, true)
    }
    live((d) => {
      const c = d.channels[activeChannel].controllers.find((x) => x.id === drag.id)
      if (!c || !snapped) return
      if (typeof part === 'object') {
        c.nodes[part.node] = snapped
        c.a = { ...c.nodes[0] }
        c.b = { ...c.nodes[c.nodes.length - 1] }
      } else if (part === 'both') {
        const ddx = snapped.x - drag.origA.x
        const ddy = snapped.y - drag.origA.y
        c.a = { x: drag.origA.x + ddx, y: drag.origA.y + ddy }
        c.b = { x: drag.origB.x + ddx, y: drag.origB.y + ddy }
        c.nodes = drag.origNodes.map((p) => ({ x: p.x + ddx, y: p.y + ddy }))
      } else if (part === 'a') {
        c.a = snapped
        if (c.kind === 'point') c.b = { ...c.a }
      } else if (part === 'b') {
        c.b = snapped
      }
      propagateSync(d, c.id)
    })
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current
    dragRef.current = null
    setGuides([])
    if (drag?.kind === 'guide') {
      // Dropping a guide back onto a ruler removes it; an add-then-remove
      // gesture nets no document change and leaves no undo entry.
      const rect = canvasRef.current?.getBoundingClientRect()
      if (rect) {
        const sx = e.clientX - rect.left
        const sy = e.clientY - rect.top
        if (sx < RULER || sy < RULER) {
          live((d) => {
            d.guides = d.guides.filter((g) => g.id !== drag.id)
          })
        }
      }
      setGuideLabel(null)
      endGesture()
      return
    }
    if (
      drag?.kind === 'controller' ||
      drag?.kind === 'radius' ||
      drag?.kind === 'path' ||
      drag?.kind === 'boundaryNode'
    ) {
      endGesture()
    }
  }

  // Double-click: finish placement, delete a node, or insert one on the spine.
  const onDoubleClick = (e: React.MouseEvent) => {
    if (pendingAdd) {
      finishPending()
      return
    }
    const canvas = canvasRef.current
    if (!canvas || !view) return
    const rect = canvas.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top

    if (pathMode) {
      const ph = hitPath(sx, sy)
      if (ph) {
        const id = ph.p.id
        if (typeof ph.part === 'object') {
          const idx = ph.part.node
          if (ph.p.nodes.length <= 2) return
          commit('Delete path node', (d) => {
            const p = d.layout.paths.find((x) => x.id === id)
            if (p && p.nodes.length > 2) p.nodes.splice(idx, 1)
          })
        } else {
          const w = toWorld(sx, sy)
          let bestI = 0
          let best = Infinity
          for (let i = 0; i + 1 < ph.p.nodes.length; i++) {
            const { d } = spineDistSide([ph.p.nodes[i], ph.p.nodes[i + 1]], w.x, w.y, false)
            if (d < best) {
              best = d
              bestI = i
            }
          }
          commit('Insert path node', (d) => {
            const p = d.layout.paths.find((x) => x.id === id)
            if (p) p.nodes.splice(bestI + 1, 0, w)
          })
        }
        return
      }
    }

    // Boundary corners: double-click a corner deletes it, an edge inserts one.
    if (boundaryEditable) {
      const bIdx = hitBoundaryNode(sx, sy)
      if (bIdx !== null) {
        if (doc.boundary.nodes.length > 3) {
          commit('Delete boundary corner', (d) => {
            if (d.boundary.nodes.length > 3) d.boundary.nodes.splice(bIdx, 1)
          })
        }
        return
      }
      const w = toWorld(sx, sy)
      const nodes = doc.boundary.nodes
      let bestI = -1
      let best = Infinity
      for (let i = 0; i < nodes.length; i++) {
        const { d } = spineDistSide([nodes[i], nodes[(i + 1) % nodes.length]], w.x, w.y, false)
        if (d < best) {
          best = d
          bestI = i
        }
      }
      if (view && best * view.scale < 8) {
        commit('Insert boundary corner', (d) => {
          d.boundary.nodes.splice(bestI + 1, 0, w)
        })
        return
      }
    }

    const hit = hitController(sx, sy)
    if (!hit || (hit.c.kind !== 'curve' && hit.c.kind !== 'polyline')) return
    const id = hit.c.id
    if (typeof hit.part === 'object') {
      const idx = hit.part.node
      if (hit.c.nodes.length <= 2) return
      commit('Delete node', (d) => {
        const c = d.channels[activeChannel].controllers.find((x) => x.id === id)
        if (!c || c.nodes.length <= 2) return
        c.nodes.splice(idx, 1)
        c.a = { ...c.nodes[0] }
        c.b = { ...c.nodes[c.nodes.length - 1] }
        propagateSync(d, c.id)
      })
    } else if (hit.part === 'both') {
      const w = toWorld(sx, sy)
      // Insert after the closest node segment (indexes are over the nodes,
      // not the sampled spine, so this works for curves too).
      let bestI = 0
      let best = Infinity
      for (let i = 0; i + 1 < hit.c.nodes.length; i++) {
        const { d } = spineDistSide([hit.c.nodes[i], hit.c.nodes[i + 1]], w.x, w.y, false)
        if (d < best) {
          best = d
          bestI = i
        }
      }
      commit('Insert node', (d) => {
        const c = d.channels[activeChannel].controllers.find((x) => x.id === id)
        if (!c) return
        c.nodes.splice(bestI + 1, 0, w)
        propagateSync(d, c.id)
      })
    }
  }

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas || !view || pendingAdd) return
    const rect = canvas.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    if (pathMode) {
      const ph = hitPath(sx, sy)
      if (ph) {
        setSelectedPath(ph.p.id)
        setMenu({ x: sx, y: sy, id: ph.p.id, target: 'path', kind: ph.p.kind })
        return
      }
    }
    const hit = hitController(sx, sy)
    if (!hit) {
      setMenu(null)
      return
    }
    setSelectedController(hit.c.id)
    setMenu({ x: sx, y: sy, id: hit.c.id, target: 'controller', kind: hit.c.kind })
  }

  // Canvas-focused keys: Tab cycles controllers, arrows nudge, Delete removes,
  // Enter finishes a curve/polyline placement, Escape exits the ruler.
  const onCanvasKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && (rulerOn || ruler)) {
      setRulerOn(false)
      setRuler(null)
      return
    }
    if (e.key === 'Enter' && pendingAdd && pendingAdd.nodes.length >= 2) {
      e.preventDefault()
      finishPending()
      return
    }
    const list = controllers
    if (e.key === 'Tab') {
      if (list.length === 0) return
      e.preventDefault()
      const idx = list.findIndex((x) => x.id === selectedController)
      const dir = e.shiftKey ? -1 : 1
      const next = list[(idx + dir + list.length) % list.length]
      setSelectedController(next.id)
      return
    }
    if (!selectedController) return
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      commit('Delete controller', (d) => {
        const l = d.channels[activeChannel].controllers
        d.channels[activeChannel].controllers = l.filter((x) => x.id !== selectedController)
      })
      setSelectedController(null)
      return
    }
    const step = e.shiftKey ? 10 : e.altKey ? 0.1 : 1
    let dx = 0
    let dy = 0
    if (e.key === 'ArrowLeft') dx = -step
    else if (e.key === 'ArrowRight') dx = step
    else if (e.key === 'ArrowUp') dy = -step
    else if (e.key === 'ArrowDown') dy = step
    if (dx !== 0 || dy !== 0) {
      e.preventDefault()
      commit('Nudge controller', (d) => {
        const c = d.channels[activeChannel].controllers.find((x) => x.id === selectedController)
        if (!c) return
        c.a = { x: c.a.x + dx, y: c.a.y + dy }
        c.b = { x: c.b.x + dx, y: c.b.y + dy }
        c.nodes = c.nodes.map((p) => ({ x: p.x + dx, y: p.y + dy }))
        propagateSync(d, c.id)
      })
    }
  }

  // Drawing.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !view || wrapSize.w === 0) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.round(wrapSize.w * dpr)
    canvas.height = Math.round(wrapSize.h * dpr)
    canvas.style.width = `${wrapSize.w}px`
    canvas.style.height = `${wrapSize.h}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const tok = makeTokens()
    ctx.fillStyle = tok('--cv-bg', '#07090d')
    ctx.fillRect(0, 0, wrapSize.w, wrapSize.h)

    ctx.save()
    ctx.translate(view.tx, view.ty)
    ctx.scale(view.scale, view.scale)

    // Canvas sheet.
    ctx.fillStyle = tok('--cv-sheet', '#0d1219')
    ctx.fillRect(0, 0, doc.canvas.width, doc.canvas.height)

    // Background image under the pattern: it shows through the holes.
    const bg = doc.background
    if (bg && bg.visible && bgRef.current?.uri === bg.dataUri) {
      const im = bgRef.current.img
      const h = bg.width * (im.height / Math.max(1, im.width))
      ctx.globalAlpha = bg.opacity
      ctx.drawImage(im, bg.x, bg.y, bg.width, h)
      ctx.globalAlpha = 1
    }

    if (panelPaths) {
      // Panel with holes punched out, drawn with the even-odd rule.
      ctx.fillStyle = tok('--cv-panel', '#2c3644')
      ctx.fill(panelPaths.path, 'evenodd')
      ctx.strokeStyle = tok('--cv-panel-line', '#55647a')
      ctx.lineWidth = 1 / view.scale
      ctx.stroke(panelPaths.outline)
    }

    // Controllers of the active channel.
    const color = tok(`--ch-${activeChannel}`, '#45e0c8')
    const handleRing = tok('--cv-handle-ring', '#0a0d12')
    const extLen = 4 * (doc.canvas.width + doc.canvas.height)
    for (const c of controllers) {
      if (!c.enabled) continue
      const isSel = c.id === selectedController

      // Unselected affectors stay out of the way: just quiet handle dots so
      // the final design reads clean; selecting one reveals its full rig.
      if (!isSel) {
        ctx.globalAlpha = 0.55
        if (c.kind === 'curve' || c.kind === 'polyline') {
          for (const n of c.nodes) quietHandle(ctx, n.x, n.y, view.scale, color)
        } else {
          if (c.kind === 'line') quietHandle(ctx, c.b.x, c.b.y, view.scale, color)
          quietHandle(ctx, c.a.x, c.a.y, view.scale, color)
        }
        ctx.globalAlpha = 1
        continue
      }

      const spine = controllerSpine(c)
      const drawPts = c.infinite && c.kind !== 'point' ? extendPts(spine, extLen) : spine

      ctx.fillStyle = color
      ctx.strokeStyle = color

      // Influence band.
      ctx.globalAlpha = 0.14
      if (c.kind === 'point') {
        ctx.beginPath()
        ctx.arc(c.a.x, c.a.y, c.radius, 0, Math.PI * 2)
        ctx.fill()
      } else if (c.singleSide === 'off') {
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.lineWidth = c.radius * 2
        strokePolyline(ctx, drawPts)
      } else {
        const sideSign = c.singleSide === 'left' ? -1 : 1
        const off = offsetSpine(drawPts, c.radius, sideSign)
        ctx.beginPath()
        ctx.moveTo(drawPts[0].x, drawPts[0].y)
        for (let i = 1; i < drawPts.length; i++) ctx.lineTo(drawPts[i].x, drawPts[i].y)
        for (let i = off.length - 1; i >= 0; i--) ctx.lineTo(off[i].x, off[i].y)
        ctx.closePath()
        ctx.fill()
      }
      ctx.globalAlpha = 1

      // Influence edge, dashed when selected: it is a drag target. Curve and
      // polyline edges only appear on selection; their per-vertex offsets can
      // spike on concave sections and would clutter the resting view.
      const showEdge = isSel || c.kind === 'point' || c.kind === 'line'
      if (showEdge) {
        ctx.lineWidth = (isSel ? 1.4 : 1) / view.scale
        ctx.globalAlpha = isSel ? 0.9 : 0.35
        ctx.setLineDash(isSel ? [5 / view.scale, 4 / view.scale] : [])
        ctx.lineCap = 'butt'
        ctx.lineJoin = 'round'
        if (c.kind === 'point') {
          ctx.beginPath()
          ctx.arc(c.a.x, c.a.y, c.radius, 0, Math.PI * 2)
          ctx.stroke()
        } else {
          const sides = c.singleSide === 'off' ? [-1, 1] : [c.singleSide === 'left' ? -1 : 1]
          for (const s of sides) strokePolyline(ctx, offsetSpine(drawPts, c.radius, s), true)
        }
        ctx.setLineDash([])
        ctx.globalAlpha = 1
      }

      // Spine.
      if (c.kind !== 'point') {
        ctx.lineWidth = (isSel ? 2.5 : 1.5) / view.scale
        strokePolyline(ctx, spine, true)
        if (c.infinite) {
          ctx.setLineDash([6 / view.scale, 5 / view.scale])
          ctx.globalAlpha = 0.55
          strokeSegment(ctx, drawPts[0], spine[0])
          strokeSegment(ctx, spine[spine.length - 1], drawPts[drawPts.length - 1])
          ctx.setLineDash([])
          ctx.globalAlpha = 1
        }
      }

      // Handles.
      if (c.kind === 'curve' || c.kind === 'polyline') {
        for (const n of c.nodes) handle(ctx, n.x, n.y, view.scale, color, handleRing, isSel)
      } else {
        if (c.kind === 'line') handle(ctx, c.b.x, c.b.y, view.scale, color, handleRing, isSel)
        handle(ctx, c.a.x, c.a.y, view.scale, color, handleRing, isSel)
      }
    }

    // Active snap guides: hairlines across the sheet at the snapped values.
    if (guides.length > 0) {
      const gAccent = tok('--accent', '#45e0c8')
      for (const g of guides) {
        ctx.strokeStyle = gAccent
        ctx.globalAlpha = g.kind === 'center' ? 0.9 : 0.55
        ctx.lineWidth = 1 / view.scale
        ctx.setLineDash(g.kind === 'center' ? [] : [5 / view.scale, 4 / view.scale])
        ctx.beginPath()
        if (g.axis === 'x') {
          ctx.moveTo(g.value, 0)
          ctx.lineTo(g.value, doc.canvas.height)
        } else {
          ctx.moveTo(0, g.value)
          ctx.lineTo(doc.canvas.width, g.value)
        }
        ctx.stroke()
      }
      ctx.setLineDash([])
      ctx.globalAlpha = 1
    }

    // Editable boundary corners.
    if (boundaryEditable) {
      const bColor = tok('--cv-panel-line', '#55647a')
      for (const nd of doc.boundary.nodes) {
        handle(ctx, nd.x, nd.y, view.scale, bColor, tok('--cv-sheet', '#0d1219'), false)
      }
    }

    // Paths (path mode only): accent spine with node handles.
    if (pathMode) {
      const pColor = tok('--accent', '#45e0c8')
      for (const p of doc.layout.paths) {
        const isSel = p.id === selectedPath
        ctx.strokeStyle = pColor
        ctx.lineWidth = (isSel ? 2.5 : 1.5) / view.scale
        strokePolyline(ctx, pathSpine(p), true)
        for (const n of p.nodes) handle(ctx, n.x, n.y, view.scale, pColor, handleRing, isSel)
      }
    }

    // Active channel's image controller rect.
    const chImg = doc.channels[activeChannel].image
    if (chImg && chImg.enabled) {
      ctx.strokeStyle = color
      ctx.globalAlpha = 0.7
      ctx.setLineDash([6 / view.scale, 4 / view.scale])
      ctx.lineWidth = 1.2 / view.scale
      ctx.strokeRect(chImg.rect.x, chImg.rect.y, chImg.rect.w, chImg.rect.h)
      ctx.setLineDash([])
      ctx.globalAlpha = 1
    }

    // Pending placement preview.
    if (pendingAdd && hover) {
      ctx.strokeStyle = color
      ctx.setLineDash([4 / view.scale, 4 / view.scale])
      ctx.lineWidth = 1.5 / view.scale
      if (pendingAdd.kind === 'line' && pendingAdd.firstPoint) {
        strokeSegment(ctx, pendingAdd.firstPoint, hover)
        handle(ctx, pendingAdd.firstPoint.x, pendingAdd.firstPoint.y, view.scale, color, handleRing, true)
      } else if (
        (pendingAdd.kind === 'curve' || pendingAdd.kind === 'polyline') &&
        pendingAdd.nodes.length > 0
      ) {
        const pts = [...pendingAdd.nodes, hover]
        const preview = pendingAdd.kind === 'curve' ? sampleCatmullRom(pts) : pts
        strokePolyline(ctx, preview, true)
        // Boundary outlines close back to the first corner.
        if (pendingAdd.target === 'boundary' && pendingAdd.nodes.length >= 2) {
          strokeSegment(ctx, hover, pendingAdd.nodes[0])
        }
        for (const n of pendingAdd.nodes) handle(ctx, n.x, n.y, view.scale, color, handleRing, true)
      }
      ctx.setLineDash([])
    }

    ctx.restore()

    // Persistent measurement guides (screen space, spanning the viewport).
    const warm = tok('--warn', '#f0b459')
    for (const g of doc.guides) {
      const s = g.value * view.scale + (g.axis === 'x' ? view.tx : view.ty)
      ctx.strokeStyle = warm
      ctx.globalAlpha = 0.75
      ctx.lineWidth = 1
      ctx.beginPath()
      if (g.axis === 'x') {
        ctx.moveTo(s, RULER)
        ctx.lineTo(s, wrapSize.h)
      } else {
        ctx.moveTo(RULER, s)
        ctx.lineTo(wrapSize.w, s)
      }
      ctx.stroke()
    }
    ctx.globalAlpha = 1

    // Edge rulers: document millimetres with zoom-adaptive ticks, the
    // boundary extent highlighted, guide notches, and a cursor marker.
    {
      const bg = tok('--bg1', '#0c1016')
      const lineCol = tok('--line', '#202a36')
      const faint = tok('--ink-faint', '#5c6a7b')
      const dim = tok('--ink-dim', '#8595a8')
      const accent = tok('--accent', '#45e0c8')
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, wrapSize.w, RULER)
      ctx.fillRect(0, 0, RULER, wrapSize.h)
      ctx.strokeStyle = lineCol
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, RULER + 0.5)
      ctx.lineTo(wrapSize.w, RULER + 0.5)
      ctx.moveTo(RULER + 0.5, 0)
      ctx.lineTo(RULER + 0.5, wrapSize.h)
      ctx.stroke()

      // Boundary extent bands.
      const bb = boundaryBBox(doc)
      ctx.fillStyle = accent
      ctx.globalAlpha = 0.4
      ctx.fillRect(bb.minX * view.scale + view.tx, RULER - 3, (bb.maxX - bb.minX) * view.scale, 3)
      ctx.fillRect(RULER - 3, bb.minY * view.scale + view.ty, 3, (bb.maxY - bb.minY) * view.scale)
      ctx.globalAlpha = 1

      // Tick spacing: the smallest nice step that keeps labels apart.
      const major =
        [1, 2, 5, 10, 20, 50, 100, 200, 500].find((s) => s * view.scale >= 48) ?? 1000
      const minor = (major / 5) * view.scale >= 5 ? major / 5 : null
      ctx.font = '9px ui-monospace, SF Mono, Menlo, monospace'
      ctx.textBaseline = 'alphabetic'

      const drawAxis = (horizontal: boolean) => {
        const size = horizontal ? wrapSize.w : wrapSize.h
        const off = horizontal ? view.tx : view.ty
        const from = (RULER - off) / view.scale
        const to = (size - off) / view.scale
        if (minor) {
          ctx.strokeStyle = faint
          ctx.globalAlpha = 0.5
          ctx.beginPath()
          for (let v = Math.floor(from / minor) * minor; v <= to; v += minor) {
            const s = v * view.scale + off
            if (s < RULER) continue
            if (horizontal) {
              ctx.moveTo(s + 0.5, RULER - 4)
              ctx.lineTo(s + 0.5, RULER)
            } else {
              ctx.moveTo(RULER - 4, s + 0.5)
              ctx.lineTo(RULER, s + 0.5)
            }
          }
          ctx.stroke()
          ctx.globalAlpha = 1
        }
        ctx.strokeStyle = faint
        ctx.fillStyle = dim
        ctx.beginPath()
        for (let v = Math.floor(from / major) * major; v <= to; v += major) {
          const s = v * view.scale + off
          if (s < RULER) continue
          if (horizontal) {
            ctx.moveTo(s + 0.5, RULER - 8)
            ctx.lineTo(s + 0.5, RULER)
          } else {
            ctx.moveTo(RULER - 8, s + 0.5)
            ctx.lineTo(RULER, s + 0.5)
          }
        }
        ctx.stroke()
        for (let v = Math.floor(from / major) * major; v <= to; v += major) {
          const s = v * view.scale + off
          if (s < RULER + 2) continue
          if (horizontal) ctx.fillText(String(v), s + 3, 9)
          else ctx.fillText(String(v), 2, s - 3)
        }
      }
      drawAxis(true)
      drawAxis(false)

      // Guide notches on their ruler.
      ctx.fillStyle = warm
      for (const g of doc.guides) {
        const s = g.value * view.scale + (g.axis === 'x' ? view.tx : view.ty)
        if (g.axis === 'x') ctx.fillRect(s - 1, 0, 2, RULER)
        else ctx.fillRect(0, s - 1, RULER, 2)
      }

      // Cursor position marker.
      if (cursorPx) {
        ctx.fillStyle = accent
        ctx.fillRect(cursorPx.x, 0, 1, RULER)
        ctx.fillRect(0, cursorPx.y, RULER, 1)
      }

      // Corner square.
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, RULER, RULER)
      ctx.strokeStyle = lineCol
      ctx.strokeRect(0.5, 0.5, RULER, RULER)
      ctx.fillStyle = faint
      ctx.fillText('mm', 4, 14)
    }

    // The mm chip while dragging a guide.
    if (guideLabel) {
      const label = `${guideLabel.axis.toUpperCase()} ${guideLabel.value.toFixed(1)} mm`
      ctx.font = '11px ui-monospace, SF Mono, Menlo, monospace'
      const tw = ctx.measureText(label).width
      const lx = Math.min(guideLabel.sx + 14, wrapSize.w - tw - 16)
      const ly = Math.max(guideLabel.sy - 14, RULER + 14)
      ctx.fillStyle = tok('--bg1', '#0c1016')
      ctx.strokeStyle = tok('--line-2', '#2e3b4a')
      ctx.lineWidth = 1
      ctx.fillRect(lx - 6, ly - 10, tw + 12, 18)
      ctx.strokeRect(lx - 6, ly - 10, tw + 12, 18)
      ctx.fillStyle = tok('--ink', '#d9e3ee')
      ctx.textBaseline = 'middle'
      ctx.fillText(label, lx, ly)
      ctx.textBaseline = 'alphabetic'
    }

    // Ruler, drawn in screen space so the line and label stay crisp.
    if (ruler) {
      const ax = ruler.a.x * view.scale + view.tx
      const ay = ruler.a.y * view.scale + view.ty
      const bx = ruler.b.x * view.scale + view.tx
      const by = ruler.b.y * view.scale + view.ty
      const accent = tok('--accent', '#45e0c8')
      ctx.strokeStyle = accent
      ctx.lineWidth = 1.4
      ctx.beginPath()
      ctx.moveTo(ax, ay)
      ctx.lineTo(bx, by)
      ctx.stroke()
      // Perpendicular end ticks.
      const len = Math.hypot(bx - ax, by - ay)
      if (len > 1e-6) {
        const nx = (-(by - ay) / len) * 6
        const ny = ((bx - ax) / len) * 6
        for (const [px, py] of [
          [ax, ay],
          [bx, by],
        ]) {
          ctx.beginPath()
          ctx.moveTo(px - nx, py - ny)
          ctx.lineTo(px + nx, py + ny)
          ctx.stroke()
        }
      }
      const mm = Math.hypot(ruler.b.x - ruler.a.x, ruler.b.y - ruler.a.y)
      const label = `${mm.toFixed(2)} mm`
      ctx.font = '11px ui-monospace, SF Mono, Menlo, monospace'
      const tw = ctx.measureText(label).width
      const lx = (ax + bx) / 2
      const ly = (ay + by) / 2 - 12
      ctx.fillStyle = tok('--bg1', '#0c1016')
      ctx.strokeStyle = tok('--line-2', '#2e3b4a')
      ctx.lineWidth = 1
      ctx.fillRect(lx - tw / 2 - 6, ly - 10, tw + 12, 18)
      ctx.strokeRect(lx - tw / 2 - 6, ly - 10, tw + 12, 18)
      ctx.fillStyle = tok('--ink', '#d9e3ee')
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label, lx, ly)
      ctx.textAlign = 'start'
      ctx.textBaseline = 'alphabetic'
    }
  }, [result, panelPaths, view, wrapSize, doc, controllers, activeChannel, selectedController, selectedPath, pathMode, pathSpine, boundaryEditable, pendingAdd, hover, theme, bgTick, ruler, guides, cursorPx, guideLabel])

  return (
    <div className="canvas-wrap" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className={[
          pendingAdd || rulerOn ? 'crosshair' : '',
          cursorPx && cursorPx.x < RULER && cursorPx.y >= RULER ? 'cursor-ew' : '',
          cursorPx && cursorPx.y < RULER && cursorPx.x >= RULER ? 'cursor-ns' : '',
        ]
          .join(' ')
          .trim()}
        tabIndex={0}
        aria-label="Pattern canvas. Scroll to zoom, drag to pan. With a controller selected: arrows nudge, Delete removes, Tab cycles."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setCursorPx(null)}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        onKeyDown={onCanvasKeyDown}
      />
      <div className="canvas-hud">
        <button
          className={rulerOn ? 'icon-btn active' : 'icon-btn'}
          onClick={() =>
            setRulerOn((on) => {
              if (on) setRuler(null)
              return !on
            })
          }
          title="Ruler: drag to measure, Esc exits"
          aria-label="Ruler"
          aria-pressed={rulerOn}
        >
          <IconRuler size={14} />
        </button>
        <button className="icon-btn" onClick={fit} title="Fit view (F)" aria-label="Fit view">
          <IconFit size={14} />
        </button>
      </div>
      {menu && (
        <div
          className="context-menu"
          style={{
            left: Math.max(4, Math.min(menu.x, (wrapRef.current?.clientWidth ?? 9999) - 176)),
            top: Math.max(4, Math.min(menu.y, (wrapRef.current?.clientHeight ?? 9999) - 88)),
          }}
          role="menu"
        >
          {menu.kind === 'curve' && (
            <button
              role="menuitem"
              onClick={() => {
                if (menu.target === 'path') {
                  commit('Convert curve path to polyline', (d) => {
                    const p = d.layout.paths.find((x) => x.id === menu.id)
                    if (!p || p.kind !== 'curve') return
                    p.nodes = sampleCatmullRom(p.nodes, 4)
                    p.kind = 'polyline'
                  })
                } else {
                  commit('Convert curve to polyline', (d) => {
                    const c = d.channels[activeChannel].controllers.find((x) => x.id === menu.id)
                    if (!c || c.kind !== 'curve') return
                    c.nodes = sampleCatmullRom(c.nodes, 4)
                    c.kind = 'polyline'
                    propagateSync(d, c.id)
                  })
                }
                setMenu(null)
              }}
            >
              Convert to polyline
            </button>
          )}
          {menu.target === 'controller' && (
            <button
              role="menuitem"
              onClick={() => {
                const newId = newControllerId()
                commit('Duplicate controller', (d) => {
                  const list = d.channels[activeChannel].controllers
                  const src = list.find((x) => x.id === menu.id)
                  if (!src) return
                  const copy = structuredClone(src)
                  copy.id = newId
                  copy.syncGroup = null
                  copy.a = { x: copy.a.x + 4, y: copy.a.y + 4 }
                  copy.b = { x: copy.b.x + 4, y: copy.b.y + 4 }
                  copy.nodes = copy.nodes.map((n) => ({ x: n.x + 4, y: n.y + 4 }))
                  list.push(copy)
                })
                setSelectedController(newId)
                setMenu(null)
              }}
            >
              Duplicate
            </button>
          )}
          <button
            role="menuitem"
            className="danger"
            onClick={() => {
              if (menu.target === 'path') {
                commit('Delete path', (d) => {
                  d.layout.paths = d.layout.paths.filter((x) => x.id !== menu.id)
                })
                setSelectedPath(null)
              } else {
                commit('Delete controller', (d) => {
                  const l = d.channels[activeChannel].controllers
                  d.channels[activeChannel].controllers = l.filter((x) => x.id !== menu.id)
                })
                setSelectedController(null)
              }
              setMenu(null)
            }}
          >
            {menu.target === 'path' ? 'Delete path' : 'Delete controller'}
          </button>
        </div>
      )}
      {!menu && <Inspector wrapRef={wrapRef} />}
    </div>
  )
}

function addRing(path: Path2D, ring: [number, number][]) {
  path.moveTo(ring[0][0], ring[0][1])
  for (let i = 1; i < ring.length; i++) path.lineTo(ring[i][0], ring[i][1])
  path.closePath()
}

function strokePolyline(ctx: CanvasRenderingContext2D, pts: Vec2[], stroke = true) {
  if (pts.length < 2) return
  ctx.beginPath()
  ctx.moveTo(pts[0].x, pts[0].y)
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y)
  if (stroke) ctx.stroke()
  else ctx.fill()
}

function strokeSegment(ctx: CanvasRenderingContext2D, a: Vec2, b: Vec2) {
  ctx.beginPath()
  ctx.moveTo(a.x, a.y)
  ctx.lineTo(b.x, b.y)
  ctx.stroke()
}

// Prepend/append points along the end directions, for drawing infinite
// extensions.
function extendPts(pts: Vec2[], ext: number): Vec2[] {
  if (pts.length < 2) return pts
  const first = pts[0]
  const second = pts[1]
  const last = pts[pts.length - 1]
  const prev = pts[pts.length - 2]
  const d1 = norm({ x: first.x - second.x, y: first.y - second.y })
  const d2 = norm({ x: last.x - prev.x, y: last.y - prev.y })
  return [
    { x: first.x + d1.x * ext, y: first.y + d1.y * ext },
    ...pts,
    { x: last.x + d2.x * ext, y: last.y + d2.y * ext },
  ]
}

function norm(v: Vec2): Vec2 {
  const len = Math.hypot(v.x, v.y) || 1
  return { x: v.x / len, y: v.y / len }
}

// Minimal marker for unselected affectors.
function quietHandle(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, color: string) {
  ctx.beginPath()
  ctx.arc(x, y, 3.5 / scale, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
}

function handle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
  color: string,
  ringColor: string,
  selected: boolean,
) {
  ctx.beginPath()
  ctx.arc(x, y, (selected ? 6 : 4.5) / scale, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
  ctx.strokeStyle = ringColor
  ctx.lineWidth = 1.5 / scale
  ctx.stroke()
}
